import type { EventContainer } from '@trello/analytics-types';

import type { TraitName } from './traits';

type Extends<T, TSubType extends T> = TSubType;

type NonWebEventBasedTraitName = Extends<
  TraitName,
  | 'segment_trello_has_learned_more_inbox_info_panel_qc_aaid'
  | 'segment_trello_has_learned_more_inbox_quick_capture_real-time_aaid'
  | 'segment_trello_has_used_inbox_quick_capture_real-time_aaid'
  | 'trello_automation_clicked_menuitem_butlerreportmenuitem'
  | 'trello_ga_modal_date'
  | 'trello_has_used_inbox_quick_capture_browser_extension'
  | 'trello_has_used_paid_planner_actions_changed'
  | 'trello_has_viewed_connectChromeInlineDialog'
  | 'trello_jira_co_users_vnext'
  | 'trello_operational_plannerdiscoveryspotlighteligibility_excluded_changed'
  | 'trello_planner_viewed_real_time_aaid'
  | 'trello_track_card_added_browserextension_changed'
  | 'trello_track_card_added_quick_capture_changed_batch'
  | 'trello_ui_button_clicked_connectaccountbutton_plannerscreen_changed'
  | 'trello_ui_button_clicked_newfeaturecta_feature_announcement_first_time'
  | 'trello_ui_button_clicked_onboardinglistspotlightdonebutton_first_time'
  | 'trello_ui_clicked_quick_capture_prompt_changed_batch'
  | 'trello_ui_flag_viewed_inviteeboardwelcomemessage_boardscreen_first_time'
  | 'trello_ui_section_viewed_workspacewelcomemessage_workspaceboardshomescreen_first_time'
  | 'trello_ui_spotlight_viewed_featureannouncementintrospotlight_changed'
  | 'trello_ui_spotlight_viewed_newinviteeinboxbuttonspotlight_newinviteeinboxbuttonspotlight_first_time'
  | 'trello_ui_spotlight_viewed_newinviteeinboxpanelspotlight_newinviteeinboxpanelspotlight_first_time'
  | 'trello_ui_spotlight_viewed_newinviteeplannerbuttonspotlight_newinviteeplannerbuttonspotlight_first_time'
  | 'trello_ui_spotlight_viewed_newinviteeplannerpanelspotlight_newinviteeplannerpanelspotlight_first_time'
  | 'trello_ui_spotlight_viewed_plannerdiscoveryspotlight_plannerdiscoveryspotlight_first_time'
  | 'trello_user_has_android_app_usage_since_2024'
  | 'trello_user_has_ios_app_usage_since_2024'
  | 'trello_viewed_chrome_qc_highlight_over_21_days_ago'
>;

type TraitEvents = {
  trello_has_viewed_connectChromeInlineDialog: {
    name: 'connectChromeInlineDialog';
    containers: EventContainer;
    attributes: {
      containerType: 'inbox';
      personalProductivity: 'inbox';
      source: 'browser';
    };
  };
  test_trait: {
    name: 'boardScreen';
    attributes: {
      test: 'test';
    };
  };
};

/**
 * Traits are often computed from fields on GASv3 events that the web client sends. If the shape of those
 * event payloads changes without updating the trait pipeline, traits can start writing wrong values to
 * entities and break user-facing behavior.
 *
 * `typeGuardTraitEvent` constrains each payload to the corresponding entry in `TraitEvents`, so edits to
 * the event object must stay in sync with that type. TypeScript will report an error when fields no
 * longer match.
 *
 * @param event - The event object to type guard.
 * @returns The event object, frozen to prevent mutation.
 * @example
 * Analytics.sendScreenEvent(
 *   typeGuardTraitEvent<'trello_has_viewed_connectChromeInlineDialog'>(
 *     {
 *       name: 'connectChromeInlineDialog',
 *       containers: formatContainers({
 *         idList: listId,
 *         idBoard: boardId,
 *         idOrganization: workspaceId,
 *       }),
 *       attributes: {
 *         containerType: 'inbox',
 *         personalProductivity: 'inbox',
 *         source: 'browser',
 *       },
 *     }
 *   )
 * )
 */
export const typeGuardTraitEvent = <T extends keyof TraitEvents>(
  event: TraitEvents[T],
): TraitEvents[T] => event;

// Below is a compile-time check that every trait has a corresponding event type.
// If a new trait is added without an entry here, TypeScript will error.
// If a trait is not derived from a web GASv3 event, add it to the NonWebEventBasedTraitName union.
const _checkAllTraitsHaveEventType: Exclude<
  TraitName,
  NonWebEventBasedTraitName | keyof TraitEvents
> extends never
  ? true
  : false = true;
// use variable to avoid unused variable TS error
void _checkAllTraitsHaveEventType;
